// ─────────────────────────────────────────────────────────────
// HotSpot Admin — Design Tokens (Blinkit-Grade Quick Commerce)
// Single source of truth for colour, radius, elevation, spacing.
// ─────────────────────────────────────────────────────────────

export const color = {
  // Neutral ink ramp — text, borders, subtle surfaces
  ink: {
    950: '#0A0F1C',
    900: '#111827',
    800: '#1F2937',
    700: '#374151',
    600: '#4B5563',
    500: '#6B7280',
    400: '#9CA3AF',
    300: '#D1D5DB',
    200: '#E5E7EB',
    100: '#F3F4F6',
    50:  '#F9FAFB',
  },

  // Primary Hotspot Brand Red
  primary: {
    900: '#7F1D1D',
    800: '#991B1B',
    700: '#B91C1C',
    600: '#DC2626',
    500: '#E3141E', // Hotspot Red Signature
    400: '#EF4444',
    300: '#FCA5A5',
    200: '#FECACA',
    100: '#FEE2E2',
    50:  '#FEF2F2',
    ink: '#FFFFFF',
  },

  // Emerald ramp for financial discounts / success indicators
  emerald: {
    900: '#064E3B',
    800: '#065F46',
    700: '#047857',
    600: '#059669',
    500: '#10B981',
    400: '#34D399',
    300: '#6EE7B7',
    200: '#A7F3D0',
    100: '#D1FAE5',
    50:  '#ECFDF5',
  },

  // Express / Quick-Commerce Yellow
  yellow: {
    700: '#B45309',
    600: '#D97706',
    500: '#FECF05', // Hotspot Express Yellow
    400: '#FCD34D',
    200: '#FDE68A',
    100: '#FEF3C7',
    50:  '#FFFBEB',
    ink: '#111827',
  },

  // Legacy gold compatibility alias
  gold: {
    700: '#B45309',
    600: '#D97706',
    500: '#FECF05',
    400: '#FCD34D',
    200: '#FDE68A',
    100: '#FEF3C7',
    50:  '#FFFBEB',
    ink: '#111827',
  },

  // Surfaces
  bg:         '#F8FAFC',
  surface:    '#FFFFFF',
  surfaceAlt: '#F1F5F9',

  // Borders & dividers
  border:       '#E2E8F0',
  borderStrong: '#CBD5E1',
  divider:      '#F1F5F9',

  // Semantic tones (soft background + strong text, for badges/alerts)
  success: { bg: '#ECFDF3', border: '#ABEFC6', text: '#027A48', solid: '#12B76A' },
  warning: { bg: '#FFFAEB', border: '#FEDF89', text: '#B54708', solid: '#F79009' },
  danger:  { bg: '#FEF2F2', border: '#FEE2E2', text: '#B91C1C', solid: '#E3141E' },
  info:    { bg: '#EFF8FF', border: '#B2DDFF', text: '#175CD3', solid: '#2E90FA' },
  neutral: { bg: '#F8FAFC', border: '#E2E8F0', text: '#334155', solid: '#64748B' },
  goldTone:{ bg: '#FFFBEB', border: '#FEF3C7', text: '#B45309', solid: '#FECF05' },
};

export const radius = {
  xs: 4,
  sm: 6,
  md: 10,
  lg: 14,
  xl: 18,
  '2xl': 24,
  pill: 999,
};

export const shadow = {
  xs: '0 1px 2px rgba(15,23,42,0.04)',
  sm: '0 1px 3px rgba(15,23,42,0.08), 0 1px 2px rgba(15,23,42,0.04)',
  md: '0 4px 6px -1px rgba(15,23,42,0.08), 0 2px 4px -2px rgba(15,23,42,0.04)',
  lg: '0 10px 15px -3px rgba(15,23,42,0.08), 0 4px 6px -4px rgba(15,23,42,0.04)',
  xl: '0 20px 25px -5px rgba(15,23,42,0.10), 0 8px 10px -6px rgba(15,23,42,0.04)',
  focusRed:    '0 0 0 3px rgba(227,20,30,0.25)',
  focusGold:   '0 0 0 3px rgba(254,207,5,0.35)',
  focusDanger: '0 0 0 3px rgba(227,20,30,0.25)',
};

// 4px base spacing scale
export const space = (n) => n * 4;

export const layout = {
  sidebarExpanded: 260,
  sidebarCollapsed: 78,
  headerHeight: 64,
  contentMax: 1560,
};

export const font = "'Outfit', 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";

const tokens = { color, radius, shadow, space, layout, font };

export default tokens;
